import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "sequences-and-compositions"
    }}>{`Sequences and Compositions`}</h1>
    <p>{`Sometimes you want to orchestrate a series of action calls into a flow. There are two ways to do this: using sequences or compositions.`}</p>
    <p>{`Sequences represent a single string of actions that are invoked in sequence, starting with the first one, then second, and so forth until the last one. At each step the result of the current action feeds into the arguments of the next one. There is no support to skip one action.`}</p>
    <p>{`If you want to execute a tree of actions, where you want to be able to evaluate the result of an action and depending on it execute a different action (think of an if/else control structure) then compositions are your best friend.`}</p>
    <h2 {...{
      "id": "sequences"
    }}>{`Sequences`}</h2>
    <p>{`Assuming that you have two actions created in a package called `}<inlineCode parentName="p">{`my-package`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{}}>{`/my-package/actionA
/my-package/actionB
`}</code></pre>
    <p>{`You can create a sequence using the `}<inlineCode parentName="p">{`--sequence`}</inlineCode>{` flag in addition to the usual command for creating an action (make sure you add the namespace to the action name; otherwise you'd see an error about not being authorized to access those resources):
`}<inlineCode parentName="p">{`aio rt:action:create mySequence --sequence /[your-namespace]/my-package/actionA,/[your-namespace]/my-package/actionB`}</inlineCode></p>
    <p>{`You can invoke this as any other action. For example:
`}<inlineCode parentName="p">{`aio rt:action:invoke --result mySequence`}</inlineCode></p>
    <h3 {...{
      "id": "sequences-and-timeout"
    }}>{`Sequences and Timeout`}</h3>
    <p>{`When invoking a sequence in a blocking manner, there is a hard limit for timeout and this limit can't be changed: 60 seconds. It means when you add up the execution time of each action that is part of a sequence, the total has to be 60 seconds or less. `}</p>
    <p>{`If we apply this limitation to the example above, then `}<inlineCode parentName="p">{`actionA`}</inlineCode>{` and `}<inlineCode parentName="p">{`actionB`}</inlineCode>{` have a total budget of 60 seconds. Although the system lets you set a higher timeout on the sequnce `}<inlineCode parentName="p">{`mySequence`}</inlineCode>{`, this value is ignored and the 60 seconds limit is enforced.`}</p>
    <p>{`If one of your actions needs more than 60 seconds, then the only solution is to invoke a non-blocking action using the OpenWhisk npm module. So, using the same example, you could have `}<inlineCode parentName="p">{`actionA`}</inlineCode>{` calling another action in a non-blocking manner. You can see an example of how to do this `}<a parentName="p" {...{
        "href": "../guides/asynchronous_calls.md"
      }}>{`here`}</a>{`.`}</p>
    <p>{`You can read more about sequences on the `}<a parentName="p" {...{
        "href": "https://github.com/apache/incubator-openwhisk/blob/master/docs/actions.md#creating-action-sequences"
      }}>{`OpenWhisk documentation page`}</a>{`.`}</p>
    <h2 {...{
      "id": "compositions"
    }}>{`Compositions`}</h2>
    <p>{`When you want to orchestrate a more complex flow without having to jam all the code in one action, Apache OpenWhisk Composer is your best friend. Composer lets you assemble actions, even other compositions together and control the executions by using control-flow structure (if, while, repeat). The result of a composition gives you a single entry point and it behaves like an action, in that it supports default params, can be placed into a package, or used as web action.`}</p>
    <h3 {...{
      "id": "install-composer"
    }}>{`Install Composer`}</h3>
    <p>{`You’ll need to install the Composer Node.js package in order to use this feature:`}</p>
    <pre><code parentName="pre" {...{}}>{`npm install -g openwhisk-composer
`}</code></pre>
    <p>{`Using this package you can create the JSON file needed for deploying a composition. The flow for creating a composition looks like:`}</p>
    <ol>
      <li parentName="ol">{`You define the compositions using JavaScript`}</li>
      <li parentName="ol">{`You run `}<inlineCode parentName="li">{`compose`}</inlineCode>{` command to generate the JSON file out of this JavaScript file`}</li>
      <li parentName="ol">{`You run `}<inlineCode parentName="li">{`deploy`}</inlineCode>{` command to deploy the composition using the JSON file you generated at the previous step`}</li>
    </ol>
    <p>{`For, example let’s assume you have three actions deployed called `}<inlineCode parentName="p">{`a`}</inlineCode>{`, `}<inlineCode parentName="p">{`b`}</inlineCode>{`, and `}<inlineCode parentName="p">{`c`}</inlineCode>{` and you want to create a composition that executes `}<inlineCode parentName="p">{`a`}</inlineCode>{` and in case of success, executes `}<inlineCode parentName="p">{`b`}</inlineCode>{`, if not it executes `}<inlineCode parentName="p">{`c`}</inlineCode>{`. You use the composer package to define this composition in a JavaScript file (\`myComp.js'): `}</p>
    <pre><code parentName="pre" {...{}}>{`const composer = require('openwhisk-composer')

module.exports = composer.if(‘a’, ‘b’, ‘c’)
`}</code></pre>
    <p>{`Now, that you have the composition defined you can generate the JSON definition needed for deployment:`}</p>
    <pre><code parentName="pre" {...{}}>{`compose myComp.js > myComp.json
`}</code></pre>
    <p>{`Time to deploy:`}</p>
    <pre><code parentName="pre" {...{}}>{`deploy compositionA myComp.json
`}</code></pre>
    <p>{`This creates a composition called `}<inlineCode parentName="p">{`compositionA`}</inlineCode>{`. You can invoke this, as any other action - `}<inlineCode parentName="p">{`aio rt:action:invoke compositionA`}</inlineCode>{`. When you do this, first action `}<inlineCode parentName="p">{`a`}</inlineCode>{` is invoked. Then, if `}<inlineCode parentName="p">{`a`}</inlineCode>{` was successful, then action `}<inlineCode parentName="p">{`b`}</inlineCode>{` is invoked; if not, action `}<inlineCode parentName="p">{`c`}</inlineCode>{` is invoked.`}</p>
    <p>{`More information:`}</p>
    <ul>
      <li parentName="ul">{`Apache OpenWhisk Composer `}<a parentName="li" {...{
          "href": "https://github.com/apache/incubator-openwhisk-composer"
        }}>{`home page`}</a></li>
      <li parentName="ul">{`For a complete list of the control-flow structure check this page: `}<a parentName="li" {...{
          "href": "https://github.com/apache/incubator-openwhisk-composer/blob/master/docs/COMBINATORS.md"
        }}>{`Combinators`}</a>{`.`}</li>
    </ul>
    <h3 {...{
      "id": "parallel-compositions"
    }}>{`Parallel Compositions`}</h3>
    <p>{`Parallel compositions are not supported by I/O Runtime.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      